import { Header } from '@components/Header';
import { routes } from '@routes';
import { Helmet } from 'react-helmet-async';
import { Link, Navigate, useParams } from 'react-router-dom';

import videosData from '../data/videos.json';
import { buildOnboardingVideoUrl } from '../helpers/buildOnboardingVideoUrl';
import { filterVideosByFolder } from '../helpers/filterVideosByFolder';
import { getOnboardingVideoHost } from '../helpers/getOnboardingVideoHost';
import { getTrainingFolderTitle } from '../helpers/getTrainingFolderTitle';
import { isTrainingLessonPath } from '../helpers/isTrainingLessonPath';
import { TTrainingVideosData } from '../types/TTrainingVideoItem';

const ASD_TEMPLATE_URL =
  'https://voicescript-onboarding.s3.amazonaws.com/Editor-Proofer/ASD/Downloads/Basic+Full+Template.docx';
const ASD_DESKTOP_URL =
  'https://voicescript-onboarding.s3.amazonaws.com/Editor-Proofer/ASD/Downloads/AutoScript+Desktop.exe';

const trainingVideos = videosData as TTrainingVideosData;

export const TrainingFolderPage = () => {
  const { role = '', folder = '' } = useParams<{ role: string; folder: string }>();
  const roleVideos = trainingVideos[role];

  if (!roleVideos) {
    return <Navigate to={routes.training.make()} replace />;
  }

  const videos = filterVideosByFolder(roleVideos, folder);
  const title = getTrainingFolderTitle(folder);
  const videoHost = getOnboardingVideoHost();
  const backRoute = role === 'Agency' ? routes.trainingAgency.make() : routes.trainingEditorProofer.make();

  return (
    <>
      <Helmet>
        <title>{title} - AutoScript Training</title>
      </Helmet>
      <Header title={title} backRoute={backRoute} />

      <main className="px-4 md:px-[30px] pt-2 pb-[70px] max-w-[860px]">
        <h2 className="text-xl font-semibold text-[var(--color-text)] m-0 mb-4">Training Videos for {title}</h2>

        {folder === 'ASD' ? (
          <section className="mb-6">
            <h3 className="text-base font-semibold text-[var(--color-text)] m-0 mb-2">Downloads</h3>
            <p className="mt-0 mb-3 text-sm leading-[150%] text-[var(--color-text-muted)]">
              Please ensure that you have downloaded and installed AutoScript Desktop Editor if you haven&apos;t done so
              already. Additionally, you can download the basic template required for your training if you are using
              AutoScript Desktop Editor.
            </p>
            <ul className="m-0 pl-5 flex flex-col gap-2 text-sm">
              <li>
                <a href={ASD_TEMPLATE_URL} download className="text-[var(--color-link)] hover:underline">
                  Basic Template
                </a>
              </li>
              <li>
                <a href={ASD_DESKTOP_URL} download className="text-[var(--color-link)] hover:underline">
                  AutoScript Desktop Editor
                </a>
              </li>
            </ul>
          </section>
        ) : null}

        {videos.length === 0 ? (
          <p className="text-sm text-[var(--color-text-muted)]">No videos available for this track.</p>
        ) : (
          <ul className="m-0 p-0 list-none flex flex-col gap-2">
            {videos.map((video) => {
              if (video.type === 'headline') {
                return (
                  <li key={`headline-${video.title}-${video.path}`} className="pt-3 first:pt-0">
                    <h3 className="text-base font-semibold text-[var(--color-text)] m-0">{video.title}</h3>
                  </li>
                );
              }

              const url = buildOnboardingVideoUrl(videoHost, video.path);

              if (!isTrainingLessonPath(video.path)) {
                return (
                  <li key={video.path}>
                    <a href={url} download className="text-sm text-[var(--color-link)] hover:underline">
                      {video.title}
                    </a>
                  </li>
                );
              }

              return (
                <li key={video.path}>
                  <Link
                    to={routes.trainingWatch.make(url)}
                    state={{ from: routes.trainingFolder.make(role, folder) }}
                    className="text-sm text-[var(--color-link)] hover:underline"
                  >
                    {video.title}
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </>
  );
};
